import type { CSSProperties, FC, ReactNode } from 'react'

import { useEffect, useId, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { LiveDot } from '@/components/generals/live-dot'

interface City {
    code: string
    x: number
    y: number
    label: 'top' | 'left' | 'right' | 'bottom'
}

const CITIES: Array<City> = [
    { code: 'RTM', x: 96, y: 118, label: 'top' },
    { code: 'HAM', x: 214, y: 72, label: 'top' },
    { code: 'CHI', x: 296, y: 170, label: 'right' },
    { code: 'SAV', x: 252, y: 298, label: 'right' },
    { code: 'SHA', x: 78, y: 270, label: 'left' },
    { code: 'SIN', x: 146, y: 392, label: 'bottom' },
    { code: 'LAX', x: 292, y: 396, label: 'right' }
]

const LANES: Array<string> = [
    'M 96 118 C 150 40, 250 80, 296 170',
    'M 214 72 C 170 66, 130 84, 96 118',
    'M 78 270 C 30 210, 40 150, 96 118',
    'M 78 270 C 140 330, 196 250, 252 298',
    'M 252 298 C 292 262, 318 220, 296 170',
    'M 146 392 C 92 372, 62 322, 78 270',
    'M 146 392 C 196 440, 252 430, 292 396',
    'M 292 396 C 318 360, 292 330, 252 298'
]

interface Parcel {
    lane: number
    duration: number
    begin: number
    tone: 'ultramarine' | 'ink'
}

const PARCELS: Array<Parcel> = [
    { lane: 0, duration: 7, begin: -1, tone: 'ultramarine' },
    { lane: 3, duration: 9, begin: -4, tone: 'ink' },
    { lane: 6, duration: 6.5, begin: -2, tone: 'ultramarine' },
    { lane: 2, duration: 8, begin: -5, tone: 'ink' },
    { lane: 4, duration: 7.5, begin: -3, tone: 'ink' }
]

interface StatusChip {
    city: string
    text: string
    tone: 'signal' | 'coral' | 'ultramarine'
    side: 'left' | 'right'
}

const CHIPS: Array<StatusChip> = [
    { city: 'CHI', text: "Gate slot rebooked", tone: 'ultramarine', side: 'left' },
    { city: 'SHA', text: "Customs hold cleared", tone: 'signal', side: 'right' },
    { city: 'SAV', text: "ETA revised +2h", tone: 'coral', side: 'left' },
    { city: 'RTM', text: "Booking confirmed", tone: 'signal', side: 'right' },
    { city: 'SIN', text: "Container released", tone: 'signal', side: 'right' }
]

const VIEW_WIDTH = 360
const VIEW_HEIGHT = 460

const LABEL_OFFSET: Record<City['label'], { dx: number, dy: number, anchor: 'start' | 'middle' | 'end' }> = {
    top: { dx: 0, dy: -16, anchor: 'middle' },
    bottom: { dx: 0, dy: 24, anchor: 'middle' },
    left: { dx: -14, dy: 4, anchor: 'end' },
    right: { dx: 14, dy: 4, anchor: 'start' }
}

const TONE_DOT = {
    signal: 'bg-signal',
    coral: 'bg-coral',
    ultramarine: 'bg-ultramarine'
}

/** The hero's quiet world-lane diagram: lanes draw in, parcels travel, status chips pop up near nodes. */
export const LaneCard: FC = (): ReactNode => {
    const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { threshold: 0.2 })
    const reduced = useReducedMotion()
    const [drawn, setDrawn] = useState(false)
    const [chipIndex, setChipIndex] = useState(0)

    useEffect(() => {
        if (inView) setDrawn(true)
    }, [inView])

    useEffect(() => {
        if (!inView || reduced) return

        const timer = window.setInterval(() => {
            setChipIndex(index => (index + 1) % CHIPS.length)
        }, 2800)

        return () => window.clearInterval(timer)
    }, [inView, reduced])

    const chip = CHIPS[chipIndex]
    const chipCity = CITIES.find(city => city.code === chip.city) ?? CITIES[0]

    const chipStyle: CSSProperties = {
        left: `${(chipCity.x / VIEW_WIDTH) * 100}%`,
        top: `${(chipCity.y / VIEW_HEIGHT) * 100}%`
    }

    return (
        <div ref={ref} className='relative mx-auto w-full max-w-[400px] rotate-[1.5deg] lg:rotate-[2.5deg]'>
            <div className='animate-float-soft'>
                <div className='relative overflow-hidden rounded-[32px] border border-ink/10 bg-paper/90 shadow-lift backdrop-blur'>
                    <div className='flex items-center justify-between px-5 pt-5'>
                        <span className='eyebrow flex items-center gap-2 text-ink-soft'>
                            <LiveDot />
                            Live lanes
                        </span>

                        <span className='eyebrow text-stone'>
                            Wk 42 · 24 boxes
                        </span>
                    </div>

                    <div className='relative mx-3 mt-2 aspect-[360/460] overflow-visible'>
                        <div aria-hidden='true' className='absolute inset-2 rounded-[22px] bg-chart opacity-70 [mask-image:radial-gradient(ellipse_at_center,black_35%,transparent_80%)]' />

                        <svg
                            viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
                            data-drawn={drawn ? 'true' : 'false'}
                            className='absolute inset-0 h-full w-full'
                            fill='none'
                            role='img'
                            aria-label='Shipping lanes between Rotterdam, Hamburg, Chicago, Savannah, Shanghai, Singapore and Los Angeles, with parcels moving along them'
                        >
                            <g stroke='rgba(22,20,15,0.06)' strokeDasharray='2 6'>
                                <ellipse cx='180' cy='232' rx='168' ry='206' />
                                <ellipse cx='180' cy='232' rx='118' ry='150' />
                                <ellipse cx='180' cy='232' rx='66' ry='92' />
                            </g>

                            {LANES.map((lane, index) => (
                                <path
                                    key={lane}
                                    id={`${id}-lane-${index}`}
                                    d={lane}
                                    pathLength={1}
                                    className='route-path'
                                    stroke='rgba(22,20,15,0.24)'
                                    strokeWidth={1.3}
                                    strokeLinecap='round'
                                    style={{ '--draw-delay': `${200 + index * 160}ms` } as CSSProperties}
                                />
                            ))}

                            {!reduced && drawn && PARCELS.map(parcel => (
                                <g key={`${parcel.lane}-${parcel.duration}`}>
                                    <rect
                                        x={-4.5}
                                        y={-3.5}
                                        width={9}
                                        height={7}
                                        rx={1.8}
                                        fill={parcel.tone === 'ultramarine' ? '#2f37ff' : '#16140f'}
                                    />

                                    <animateMotion
                                        dur={`${parcel.duration}s`}
                                        begin={`${parcel.begin}s`}
                                        repeatCount='indefinite'
                                        rotate='auto'
                                    >
                                        <mpath href={`#${id}-lane-${parcel.lane}`} />
                                    </animateMotion>
                                </g>
                            ))}

                            {CITIES.map((city, index) => {
                                const offset = LABEL_OFFSET[city.label]

                                return (
                                    <g
                                        key={city.code}
                                        className='route-node'
                                        style={{ '--draw-delay': `${700 + index * 140}ms` } as CSSProperties}
                                    >
                                        <circle cx={city.x} cy={city.y} r={9} fill='#fbf8f2' stroke='rgba(22,20,15,0.22)' strokeWidth={1.1} />
                                        <circle cx={city.x} cy={city.y} r={3.4} fill={city.code === chip.city ? '#2f37ff' : '#16140f'} />

                                        <text
                                            x={city.x + offset.dx}
                                            y={city.y + offset.dy}
                                            textAnchor={offset.anchor}
                                            className='font-mono'
                                            fontSize='10'
                                            letterSpacing='1.4'
                                            fill='rgba(22,20,15,0.55)'
                                        >
                                            {city.code}
                                        </text>
                                    </g>
                                )
                            })}
                        </svg>

                        <div
                            key={chipIndex}
                            aria-hidden='true'
                            style={chipStyle}
                            className={cn(
                                'absolute z-10 -mt-12',
                                chip.side === 'left' ? '-translate-x-[calc(100%_-_14px)]' : '-translate-x-[14px]'
                            )}
                        >
                            <span className='flex animate-chip-pop items-center gap-2 whitespace-nowrap rounded-full border border-ink/10 bg-paper px-3 py-1.5 text-[12px] font-medium text-ink shadow-soft'>
                                <span className={cn('size-1.5 rounded-full', TONE_DOT[chip.tone])} />
                                {chip.text}
                            </span>
                        </div>
                    </div>

                    <dl className='grid grid-cols-3 border-t border-ink/10'>
                        {[
                            { label: 'On time', value: '97.4%' },
                            { label: 'Holds', value: '3' },
                            { label: 'Reroutes', value: '9' }
                        ].map((stat, index) => (
                            <div key={stat.label} className={cn('min-w-0 px-4 py-4 sm:px-5', index > 0 && 'border-l border-ink/10')}>
                                <dt className='eyebrow text-stone'>
                                    {stat.label}
                                </dt>

                                <dd className='mt-1 font-heading text-lg font-semibold tracking-tight text-ink'>
                                    {stat.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        </div>
    )
}
